import { useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { ArrowRight, ChevronDown, ChevronRight, Folder, GripVertical, ListTree, Search } from 'lucide-react'
import { useTasks } from '../TaskProvider'
import { taskAncestors } from '../taskTree'
import { branchIds, organizationRows } from '../taskOrganization'
import type { WritingTask } from '../types'
import { WorkspaceDialog } from './WorkspaceDialog'
import { useRouteState } from '../routeState'
import { useRefreshGuard } from '../refreshGuards'

export function TaskOrganizer({ onClose, source, onChoose, initialScope = 'team' }: {
  onClose: () => void
  source?: WritingTask
  onChoose?: (parentId: string) => void
  initialScope?: WritingTask['scope']
}) {
  const store = useTasks()
  const [routeScope, setScope] = useRouteState<WritingTask['scope']>('task-organize-scope', initialScope, ['team', 'personal'])
  const scope = source?.scope || routeScope
  const [selectedId, setSelectedId] = useState(source?.id || '')
  const [leftExpanded, setLeftExpanded] = useState(new Set<string>())
  const [rightExpanded, setRightExpanded] = useState(new Set<string>())
  const [leftSearch, setLeftSearch] = useState(''), [rightSearch, setRightSearch] = useState('')
  const [error, setError] = useState(''), [notice, setNotice] = useState('')
  const [over, setOver] = useState<string | null>(null), [dragging, setDragging] = useState(false)
  useRefreshGuard(dragging, 'Finish moving your task before refreshing.')
  const dialog = useRef<HTMLDivElement>(null)
  const gesture = useRef<{ id: string; x: number; y: number; active: boolean; over: string | null } | null>(null)
  const touchDrop = useRef<{ id: string; parentId: string } | null>(null)
  const movePending = useRef(false)
  const tasks = useMemo(() => store.tasks.filter(t => t.scope === scope && (scope === 'team' || t.ownerId === store.user.id)), [store.tasks, scope, store.user.id])
  const byId = useMemo(() => new Map(tasks.map(task => [task.id, task])), [tasks])
  const selected = source || tasks.find(t => t.id === selectedId && t.status !== 'done' && !t.archived)
  const forbidden = useMemo(() => selected ? branchIds(tasks, selected.id) : new Set<string>(), [tasks, selected?.id])
  const left = source ? [{ task: source, depth: 0, hasChildren: false }] : organizationRows(tasks, leftExpanded, leftSearch)
  const right = organizationRows(tasks, rightExpanded, rightSearch)
  const toggle = (side: 'left' | 'right', id: string) => (side === 'left' ? setLeftExpanded : setRightExpanded)(previous => {
    const next = new Set(previous); next.has(id) ? next.delete(id) : next.add(id); return next
  })
  const move = async (id: string, parentId: string) => {
    if (movePending.current || store.busy) return
    const task = source || tasks.find(t => t.id === id)
    const parent = byId.get(parentId)
    if (!task || (!source && (task.status === 'done' || task.archived)) ||
      (parentId && (!parent || parent.status === 'done' || parent.archived)) || branchIds(tasks, id).has(parentId)) return
    if (onChoose) { onChoose(parentId); return }
    if ((task.parentId || '') === parentId) { setNotice('This task is already here.'); return }
    movePending.current = true; setError(''); setNotice('')
    try {
      await store.save({ parentId }, task)
      // Both panes read this one authoritative task store. Moving only changes
      // the existing root's parent; descendants keep their IDs and parents.
      const reveal = [...taskAncestors(tasks, parentId).map(t => t.id), parentId]
      setLeftExpanded(old => new Set([...old, ...reveal]))
      setRightExpanded(old => new Set([...old, ...reveal]))
      setLeftSearch(''); setRightSearch('')
      setNotice(`Moved “${task.title}” ${parentId ? 'into “' + tasks.find(t => t.id === parentId)?.title + '”' : 'to the top level'}. Both views are updated.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'This task could not be moved.')
      await store.refresh()
    } finally { movePending.current = false }
  }
  const hit = (x: number, y: number, id: string) => {
    const element = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-organizer-destination]')
    const target = element && dialog.current?.contains(element) ? element.dataset.organizerDestination! : null
    return target !== null && !(selected?.id === id ? forbidden : branchIds(tasks, id)).has(target) ? target : null
  }
  const startDrag = (event: ReactPointerEvent<HTMLButtonElement>, id: string) => {
    if (event.button !== 0 || store.busy) return
    touchDrop.current = null
    setSelectedId(id); setNotice('')
    gesture.current = { id, x: event.clientX, y: event.clientY, active: false, over: null }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const duringDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const current = gesture.current
    if (!current) return
    if (!current.active && Math.hypot(event.clientX - current.x, event.clientY - current.y) < 5) return
    current.active = true; current.x = event.clientX; current.y = event.clientY
    current.over = hit(current.x, current.y, current.id)
    setDragging(true); setOver(current.over)
  }
  const endDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const current = gesture.current
    gesture.current = null; setDragging(false); setOver(null)
    if (current?.active) {
      const target = hit(event.clientX, event.clientY, current.id)
      if (target !== null) {
        if (event.pointerType === 'touch') touchDrop.current = { id: current.id, parentId: target }
        else void move(current.id, target)
      }
    }
  }
  useEffect(() => {
    if (!dragging) return
    let frame = 0
    const scroll = () => {
      const current = gesture.current
      if (current) {
        const pane = document.elementFromPoint(current.x, current.y)?.closest<HTMLElement>('.organizer-tree')
        if (pane && dialog.current?.contains(pane)) {
          const box = pane.getBoundingClientRect()
          const distance = current.y < box.top + 48 ? -10 : current.y > box.bottom - 48 ? 10 : 0
          if (distance) { pane.scrollTop += distance; current.over = hit(current.x, current.y, current.id); setOver(current.over) }
        }
      }
      frame = requestAnimationFrame(scroll)
    }
    frame = requestAnimationFrame(scroll)
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); gesture.current = null; setDragging(false); setOver(null) }
    }
    window.addEventListener('keydown', escape, true)
    return () => { cancelAnimationFrame(frame); window.removeEventListener('keydown', escape, true) }
  }, [dragging, tasks])
  useEffect(() => {
    if (!dragging || !over) return
    const timer = window.setTimeout(() => setRightExpanded(old => new Set([...old, over])), 650)
    return () => window.clearTimeout(timer)
  }, [dragging, over])
  const path = selected ? [...taskAncestors(tasks, selected.id).map(t => t.title), selected.title].join(' / ') : ''
  // The picker also opens from a scrolling form dialog. Keep its layout and
  // touch hit targets independent of that form's labels and scroll container.
  return createPortal(<WorkspaceDialog title={onChoose ? 'Choose parent' : 'Organize tasks'} eyebrow="EVERY BRANCH HAS A PLACE" className="task-organizer-dialog" initialFocus="close" onClose={() => { if (!store.busy) onClose() }}>
    <div className="task-organizer" ref={dialog}>
      <div className="organizer-intro">
        <p>{onChoose ? 'Drop this task onto its parent, or tap a destination.' : 'Drag a task from the left into a parent on the right, or select it and tap its destination. Moves save immediately. Completed and archived tasks are hidden.'}</p>
        {!source && <select aria-label="Organize visibility" value={scope} disabled={store.busy} onChange={e => { setScope(e.target.value as typeof scope); setSelectedId(''); setNotice('') }}>
          <option value="team">Team tasks</option><option value="personal">Personal tasks</option>
        </select>}
      </div>
      <div className="organizer-panes">
        {(['left', 'right'] as const).map(side => {
          const isLeft = side === 'left', expanded = isLeft ? leftExpanded : rightExpanded
          const rows = isLeft ? left : right, search = isLeft ? leftSearch : rightSearch
          return <section className="organizer-pane" key={side} aria-label={isLeft ? 'Source tasks' : 'Destination hierarchy'}>
            <header><strong>{isLeft ? source ? 'This task' : '1. Pick a task' : '2. Choose its parent'}</strong>
              {!(isLeft && source) && <button className="text-button" type="button" onClick={() => (isLeft ? setLeftExpanded : setRightExpanded)(new Set())}>Collapse all</button>}
            </header>
            {!(isLeft && source) && <label className="organizer-search"><Search size={14} /><input aria-label={isLeft ? 'Find source task' : 'Find destination'} value={search}
              placeholder="Find in this tree…" onChange={e => (isLeft ? setLeftSearch : setRightSearch)(e.target.value)} /></label>}
            <div className="organizer-tree" tabIndex={0}>
              {!isLeft && <OrganizerAction className={'organizer-root' + (over === '' ? ' is-drop-target' : '')} data-organizer-destination=""
                disabled={!selected || store.busy} onActivate={() => { if (selected) void move(selected.id, '') }}><ListTree size={16} />Top level · no parent</OrganizerAction>}
              {rows.map(({ task, depth, hasChildren }) => <div key={task.id} className={'organizer-row' + (isLeft && selected?.id === task.id ? ' is-selected' : '') + (!isLeft && over === task.id ? ' is-drop-target' : '') + (!isLeft && forbidden.has(task.id) ? ' is-unavailable' : '')}
                data-task-id={task.id} data-parent-id={task.parentId || ''} data-organizer-destination={isLeft ? undefined : task.id}
                style={{ '--organizer-depth': Math.min(depth, 6) } as React.CSSProperties}>
                <button type="button" className="organizer-expand" aria-label={(expanded.has(task.id) ? 'Collapse ' : 'Expand ') + task.title}
                  aria-expanded={hasChildren ? expanded.has(task.id) || !!search : undefined} disabled={!hasChildren} onClick={() => toggle(side, task.id)}>
                  {hasChildren ? expanded.has(task.id) || search ? <ChevronDown size={15} /> : <ChevronRight size={15} /> : null}
                </button>
                {isLeft && <button type="button" className="organizer-grip" aria-label={'Drag ' + task.title} disabled={store.busy}
                  onPointerDown={e => startDrag(e, task.id)} onPointerMove={duringDrag} onPointerUp={endDrag}
                  onPointerCancel={() => { gesture.current = null; touchDrop.current = null; setDragging(false); setOver(null) }}
                  onTouchEnd={event => {
                    const drop = touchDrop.current
                    touchDrop.current = null
                    if (!drop) return
                    event.preventDefault(); event.stopPropagation()
                    void move(drop.id, drop.parentId)
                  }}
                  onClick={() => setSelectedId(task.id)}><GripVertical size={16} /></button>}
                <OrganizerAction className="organizer-label" disabled={store.busy || (!isLeft && (!selected || forbidden.has(task.id)))}
                  aria-label={(isLeft ? 'Select ' : 'Move into ') + task.title} aria-pressed={isLeft ? selected?.id === task.id : undefined}
                  title={task.title + (task.parentId ? ' · Parent: ' + (byId.get(task.parentId)?.title || 'Unavailable') : ' · Top level')}
                  onActivate={() => { if (isLeft) setSelectedId(task.id); else if (selected) void move(selected.id, task.id) }}>
                  {task.kind === 'group' && <Folder size={14} />}<span>{task.title}<small>{task.kind === 'group' ? task.reference ? 'Automatic group' : 'Group' : task.status === 'done' ? 'Completed' : 'Task'}{depth > 6 ? ' · level ' + (depth + 1) : ''}</small></span>
                </OrganizerAction>
              </div>)}
              {!rows.length && <p className="organizer-empty">{search ? 'No matching open tasks.' : 'No open tasks here.'}</p>}
            </div>
          </section>
        })}
      </div>
      <footer className="organizer-footer">
        <p role="status">{store.busy ? 'Saving move…' : notice || (selected ? <><ArrowRight size={14} />{path}</> : 'Select a task to start. Its subtasks move with it.')}</p>
        {error && <p role="alert" className="task-editor-error">{error}</p>}
        <button type="button" className="button" disabled={store.busy} onClick={onClose}>{onChoose ? 'Cancel' : 'Done'}</button>
      </footer>
    </div>
  </WorkspaceDialog>, document.body)
}

function OrganizerAction({ onActivate, ...props }: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & { onActivate: () => void }) {
  const press = useRef<{ id: number; x: number; y: number } | null>(null)
  const touchHandled = useRef(false)
  const touchReady = useRef(false)
  return <button {...props} type="button" onPointerDown={event => {
    touchHandled.current = false
    touchReady.current = false
    press.current = event.pointerType === 'touch' && event.isPrimary ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null
  }} onPointerMove={event => {
    const start = press.current
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) press.current = null
  }} onPointerCancel={() => { press.current = null }} onPointerUp={event => {
    const start = press.current
    press.current = null
    if (!start || start.id !== event.pointerId || event.currentTarget.disabled ||
      Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8 ||
      !event.currentTarget.contains(document.elementFromPoint(event.clientX, event.clientY))) return
    // Some touch taps have no compatibility click. Activate at touchend,
    // canceling that click before closing a picker so it cannot hit the form
    // underneath in WebKit. Keep pointer validation to exclude scroll gestures.
    touchHandled.current = true
    touchReady.current = true
    event.currentTarget.focus({ preventScroll: true })
  }} onTouchEnd={event => {
    if (!touchReady.current) return
    touchReady.current = false
    event.preventDefault()
    event.stopPropagation()
    onActivate()
  }} onClick={event => {
    // Touch browsers that do emit click must not activate the same move twice.
    // Keyboard/assistive clicks have detail 0; a new mouse press resets this flag.
    if (touchHandled.current && event.detail !== 0) return
    onActivate()
  }} />
}
